import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {SUPPORTED_LOCALES} from '@/initializers/i18next';
import {FieldMetadata} from '@/interfaces/form';

interface UseAdvisorProfileFormSchemaProps {
  fieldsMetadata: {
    cons8_generalAgentName: FieldMetadata;
    cons9_generalAgentCode: FieldMetadata;
    cons25_advisorCode: FieldMetadata;
    cons10_generalAgentEmail: FieldMetadata;
    cons11_generalAgentEmail_confirmation: FieldMetadata;
  };
}

export const useAdvisorProfileFormSchema = ({fieldsMetadata}: UseAdvisorProfileFormSchemaProps) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  return useMemo(
    () =>
      Yup.object().shape({
        language: Yup.string().oneOf(SUPPORTED_LOCALES).required(t('errors.required')),
        cons1_lastName: Yup.string()
          .max(25, t('errors.max', {value: 25}))
          .noDangerousContent(t('errors.content-not-allowed'))
          .required(t('errors.required')),
        cons2_firstName: Yup.string()
          .max(25, t('errors.max', {value: 25}))
          .noDangerousContent(t('errors.content-not-allowed'))
          .required(t('errors.required')),
        cons4_email: Yup.string()
          .email(t('errors.email'))
          .max(50, t('errors.max', {value: 50}))
          .required(t('errors.required')),
        cons5_email_confirmation: Yup.string()
          .email(t('errors.email'))
          .max(50, t('errors.max', {value: 50}))
          .oneOf([Yup.ref('cons4_email')], t('errors.email-confirmation-must-match'))
          .required(t('errors.required')),
        cons6_phoneNumber: Yup.string()
          .length(10, t('errors.length', {value: 10}))
          .required(t('errors.required')),
        cons7_phoneExtension: Yup.string()
          .max(6, t('errors.max', {value: 6}))
          .nullable(),
        cons8_generalAgentName: Yup.string().when(_ => {
          return fieldsMetadata.cons8_generalAgentName.isRequired
            ? Yup.string()
                .max(50, t('errors.max', {value: 50}))
                .noDangerousContent(t('errors.content-not-allowed'))
                .required(t('errors.required'))
            : Yup.string()
                .max(50, t('errors.max', {value: 50}))
                .noDangerousContent(t('errors.content-not-allowed'))
                .optional();
        }),
        cons9_generalAgentCode: Yup.string()
          .length(4, t('errors.length', {value: 4}))
          .when(_ => {
            return fieldsMetadata.cons9_generalAgentCode.isRequired
              ? Yup.string().required(t('errors.required'))
              : Yup.string().optional();
          }),
        cons25_advisorCode: Yup.string()
          .min(3, t('errors.min', {value: 3}))
          .max(6, t('errors.max', {value: 6}))
          .when(_ => {
            return fieldsMetadata.cons25_advisorCode.isRequired
              ? Yup.string().required(t('errors.required'))
              : Yup.string().optional();
          }),
        altAdvisorCode1: Yup.string()
          .min(3, t('errors.min', {value: 3}))
          .max(6, t('errors.max', {value: 6}))
          .nullable(),
        altAdvisorCode2: Yup.string()
          .min(3, t('errors.min', {value: 3}))
          .max(6, t('errors.max', {value: 6}))
          .nullable(),
        altAdvisorCode3: Yup.string()
          .min(3, t('errors.min', {value: 3}))
          .max(6, t('errors.max', {value: 6}))
          .nullable(),
        altAdvisorCode4: Yup.string()
          .min(3, t('errors.min', {value: 3}))
          .max(6, t('errors.max', {value: 6}))
          .nullable(),
        cons10_generalAgentEmail: Yup.string()
          .email(t('errors.email'))
          .max(50, t('errors.max', {value: 50}))
          .when([], {
            is: () => fieldsMetadata.cons10_generalAgentEmail.isRequired,
            then: schema => schema.required(t('errors.required')),
            otherwise: schema => schema.optional(),
          }),
        cons11_generalAgentEmail_confirmation: Yup.string()
          .email(t('errors.email'))
          .max(50, t('errors.max', {value: 50}))
          .oneOf([Yup.ref('cons10_generalAgentEmail')], t('errors.email-confirmation-must-match'))
          .when([], {
            is: () => fieldsMetadata.cons11_generalAgentEmail_confirmation.isRequired,
            then: schema => schema.required(t('errors.required')),
            otherwise: schema => schema.optional(),
          }),
        cons16_isFundServe: Yup.string().oneOf(['true', 'false']).required(t('errors.required')),
      }),
    [fieldsMetadata, t],
  );
};
